"use client"

import { useState } from "react"

import { Check } from "@medusajs/icons"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"

export type ComparisonContent = {
  title: string
  subtitle: string
  beforeBadge: string
  afterBadge: string
  before: string[]
  after: string[]
  /** 后台传的图；为空时用内置占位图。 */
  beforeImage?: string | null
  afterImage?: string | null
}

/**
 * Before/after feature list next to a draggable image comparison.
 *
 * 2026-11-02（需求 11.2）：文案与配图改为由服务端壳（`with-comparison.tsx`）
 * 从后台 `home-content` 读好后传入；本组件只负责拖动滑块那点交互。
 *
 * The section id backs the Nav /#preview anchor.
 */
export default function FeatureSectionWithComparisonClient({
  title,
  subtitle,
  beforeBadge,
  afterBadge,
  before,
  after,
  beforeImage,
  afterImage,
}: ComparisonContent) {
  const beforeSrc = beforeImage ?? "/images/comparison-code-before.png"
  const afterSrc = afterImage ?? "/images/comparison-code-after.png"

  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)

  const handleMouseDown = () => {
    setIsDragging(true)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const percentage = Math.max(0, Math.min((x / rect.width) * 100, 100))

    setSliderPosition(percentage)
  }

  return (
    <section
      id="preview"
      className="container mx-auto space-y-8 px-4 py-24 md:px-6 2xl:max-w-[1400px] scroll-mt-20"
    >
      <div className="space-y-4 text-center">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          {title}
        </h2>
        <p className="text-muted-foreground mx-auto max-w-[700px] md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
          {subtitle}
        </p>
      </div>

      <div className="grid items-center gap-8 lg:grid-cols-2">
        <Card className="p-0">
          <CardContent className="p-6">
            <div className="grid gap-6">
              <div>
                <Badge variant="secondary" className="mb-2">
                  {beforeBadge}
                </Badge>
                <ul className="space-y-4">
                  {before.map((feature) => (
                    <li
                      key={feature}
                      className="text-muted-foreground flex items-center gap-3"
                    >
                      <div className="bg-muted size-2 rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <Badge className="mb-2">{afterBadge}</Badge>
                <ul className="space-y-4">
                  {after.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <Check className="text-primary size-5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <div
          className="bg-muted relative aspect-[4/3] cursor-ew-resize select-none overflow-hidden rounded-lg"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          <div className="absolute inset-0">
            {/* 这两张图由运营在后台上传、宽高比不定；next/image 需要固定尺寸
                或 fill+父级定位，这里用裸 img + object-cover 才不会裁错/跳版。 */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={afterSrc}
              alt={afterBadge}
              className="pointer-events-none object-cover"
              draggable={false}
            />
          </div>
          <div
            className="absolute inset-0"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={beforeSrc}
              alt={beforeBadge}
              className="pointer-events-none object-cover"
              draggable={false}
            />
          </div>
          <div
            className="absolute top-0 bottom-0 w-1 cursor-ew-resize bg-white"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="border-primary absolute top-1/2 size-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 bg-white" />
          </div>
        </div>
      </div>
    </section>
  )
}
